import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { BROKER, INSURANCE_TYPES } from "@/lib/constants";
import { Calendar, CheckCircle2, Mail, ArrowRight } from "lucide-react";

export default function ThankYouPage() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const low = parseFloat(params.get("low") || "0");
  const high = parseFloat(params.get("high") || "0");
  const type = params.get("type");
  const coverage = parseInt(params.get("coverage") || "0", 10);
  const typeName = INSURANCE_TYPES.find((t) => t.id === type)?.name || "Life Insurance";

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-pp-bone pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pp-forest text-pp-bone mb-6">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-pp-forest-deep leading-tight">
              Your estimate is ready.
            </h1>
            <p className="mt-5 text-lg text-pp-text-secondary max-w-xl mx-auto">
              Based on what you shared, here's an{" "}
              <span className="italic">illustrative</span> monthly premium
              range. Darryl will reach out to refine carrier matches and lock
              in your best rate.
            </p>
          </div>

          <div
            data-testid="estimate-card"
            className="bg-white rounded-3xl border border-pp-border-subtle p-8 sm:p-12 shadow-[0_20px_50px_rgba(26,46,38,0.06)] mb-8"
          >
            <div className="text-xs uppercase tracking-[0.2em] text-pp-forest font-bold mb-3">
              Estimated monthly premium
            </div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-sans font-semibold text-6xl sm:text-7xl text-pp-forest-deep tracking-tight">
                ${low.toFixed(0)}
              </span>
              <span className="text-2xl text-pp-text-secondary">–</span>
              <span className="font-sans font-semibold text-6xl sm:text-7xl text-pp-terracotta tracking-tight">
                ${high.toFixed(0)}
              </span>
              <span className="text-pp-text-secondary text-base">/ month</span>
            </div>
            <div className="mt-6 pt-6 border-t border-pp-border-subtle grid sm:grid-cols-3 gap-6 text-sm">
              <div>
                <div className="text-pp-text-secondary text-xs uppercase tracking-wider mb-1">
                  Coverage
                </div>
                <div className="text-pp-forest-deep font-medium text-lg">
                  ${coverage.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-pp-text-secondary text-xs uppercase tracking-wider mb-1">
                  Product
                </div>
                <div className="text-pp-forest-deep font-medium text-lg">
                  {typeName}
                </div>
              </div>
              <div>
                <div className="text-pp-text-secondary text-xs uppercase tracking-wider mb-1">
                  Quote ID
                </div>
                <div className="text-pp-forest-deep font-mono text-xs break-all">
                  {id}
                </div>
              </div>
            </div>
            <p className="mt-6 text-xs text-pp-text-secondary leading-relaxed">
              This is an illustrative estimate only. Final rates depend on
              carrier-specific underwriting (including medical history & exam,
              if required). Not a binding offer of coverage.
            </p>
          </div>

          <div
            data-testid="rating-disclaimer"
            className="bg-white rounded-2xl border border-pp-border-subtle p-6 sm:p-8 mb-8"
          >
            <div className="text-xs uppercase tracking-[0.2em] text-pp-forest font-bold mb-3">
              How we calculated this
            </div>
            <ul className="space-y-3 text-sm text-pp-forest-deep leading-relaxed">
              <li className="flex gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-pp-terracotta flex-shrink-0" />
                <span>
                  <b>{typeName}</b>
                  {type === "term" && " is pure protection — the most affordable way to insure your income years."}
                  {type === "whole" && " builds guaranteed cash value alongside lifetime coverage."}
                  {type === "iul" && " offers market-linked growth potential with downside protection."}
                  {type === "final_expense" && " is simplified whole life designed to cover end-of-life expenses."}
                  {type === "return_of_premium" && " returns 100% of your premiums if you outlive the term."}
                </span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-pp-terracotta flex-shrink-0" />
                <span>
                  Estimate assumes a <b>Standard Non-Smoker</b> rating class.
                  Actual class is determined by carrier underwriting (Preferred Plus / Preferred / Standard / Sub-standard).
                </span>
              </li>
            </ul>
            <blockquote
              data-testid="tobacco-note"
              className="mt-5 border-l-4 border-pp-terracotta bg-pp-cream/60 px-5 py-4 italic text-sm text-pp-text-secondary"
            >
              <b className="not-italic text-pp-forest-deep">Note:</b> Non-smokers
              typically qualify for significantly discounted pricing. If you use
              tobacco or nicotine products, your actual cost will be higher
              than the estimate shown here.
            </blockquote>
            <p className="mt-5 text-sm text-pp-text-secondary leading-relaxed">
              This is an educational estimate — book a 20-minute call below or complete a
              full application for a <b className="text-pp-forest-deep">binding, underwritten rate</b>.
            </p>
          </div>

          <div
            data-testid="next-steps-card"
            className="bg-pp-forest text-pp-bone rounded-3xl p-8 sm:p-12"
          >
            <div className="flex flex-col md:flex-row gap-8 md:items-center">
              <img
                src={BROKER.photo}
                alt="Darryl Kelly"
                className="w-24 h-24 rounded-full object-cover border-4 border-pp-bone/20"
              />
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-pp-bone/70 mb-2">
                  Next step
                </div>
                <h2 className="font-serif text-3xl leading-tight">
                  Book a free 20-minute call with Darryl
                </h2>
                <p className="mt-3 text-pp-bone/80">
                  We'll review your estimate, compare top carriers for your
                  health class, and answer every question—no pressure.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href={BROKER.calendarUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      data-testid="thanks-book-btn"
                      className="bg-pp-terracotta hover:bg-pp-terracotta-dark text-white rounded-full px-8 py-5"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Book a time
                    </Button>
                  </a>
                  <a href={`mailto:${BROKER.email}`}>
                    <Button
                      data-testid="thanks-email-btn"
                      variant="outline"
                      className="bg-transparent border-pp-bone/40 text-pp-bone hover:bg-pp-bone hover:text-pp-forest-deep rounded-full px-8 py-5"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email Darryl
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/"
              data-testid="thanks-home-link"
              className="inline-flex items-center gap-2 text-pp-forest hover:text-pp-forest-dark text-sm"
            >
              Return to homepage <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
